/**
 * DocksPage — "Docks" device listing (May 2026 Figma).
 *
 * Toolbar:
 *   [🔍]  Docks
 *         N TOTAL · N ACTIVE · N OFFLINE          [⋯] [+ Add dock] [▤]
 *
 * Table columns:
 *   [ ] DOCK NAME ↑   DEVICE IP   SERIAL NUMBER   SITE   FIRMWARE   STATUS   [pen][trash]
 *
 * Backed by dockService endpoints: /api/orgs/{orgId}/docks.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import dockService from '../services/dockService';
import DockDrawer from '../components/docks/DockDrawer';

const PAGE_SIZE = 50;

function deriveStatus(d) {
  const s = String(d.status || d.currentStatus || '').toUpperCase();
  if (s === 'OFFLINE' || s === 'INACTIVE')
    return { key: 'offline', label: 'Offline', cls: 'cam-status cam-status-offline', icon: 'fa-circle-notch' };
  if (s === 'ERROR' || s === 'FAILED')
    return { key: 'error', label: 'Error', cls: 'cam-status cam-status-error', icon: 'fa-times' };
  return { key: 'active', label: 'Online', cls: 'cam-status cam-status-active', icon: 'fa-circle' };
}

export default function DocksPage() {
  const { orgId, siteId } = useTenant();

  const [rows, setRows]               = useState([]);
  const [totalCount, setTotalCount]   = useState(0);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [search, setSearch]           = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showSearch, setShowSearch]   = useState(false);
  const [selected, setSelected]       = useState(() => new Set());
  const [sort, setSort]               = useState({ key: 'dockName', asc: true });
  const [editingDock, setEditingDock] = useState(null);   // existing dock object
  const [adding, setAdding]           = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [pageOffset, setPageOffset]   = useState(0);
  const refreshList = () => setRefreshTick((t) => t + 1);

  // Debounce search input so we don't fire a request on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPageOffset(0); // reset to first page on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!orgId) { setRows([]); setTotalCount(0); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const opts = {
      siteId: siteId || undefined,
      limit: PAGE_SIZE,
      offset: pageOffset,
      sortBy: 'deviceId',
      sortOrder: 'asc',
    };

    const fetchFn = debouncedSearch
      ? dockService.searchDocks(orgId, { ...opts, q: debouncedSearch })
      : dockService.listDocks(orgId, opts);

    fetchFn
      .then((res) => {
        if (cancelled) return;
        setRows(res?.content ?? res?.items ?? []);
        setTotalCount(res?.totalElements ?? res?.total ?? 0);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.message ?? e.message ?? 'Failed to load docks');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, siteId, refreshTick, debouncedSearch, pageOffset]);

  const summary = useMemo(() => {
    let active = 0, offline = 0;
    rows.forEach((d) => {
      const s = deriveStatus(d);
      if (s.key === 'active') active++;
      else if (s.key === 'offline') offline++;
    });
    return { total: totalCount, active, offline };
  }, [rows, totalCount]);

  // Client-side sort only (search is server-side now)
  const filteredSorted = useMemo(() => {
    const get = (d) => {
      switch (sort.key) {
        case 'dockName':     return d.dockName || '';
        case 'ipAddress':    return d.ipAddress || '';
        case 'serialNumber': return d.serialNumber || '';
        case 'siteName':     return d.siteName || '';
        case 'firmware':     return d.firmwareVersion || '';
        case 'status':       return deriveStatus(d).label;
        default:             return '';
      }
    };
    return [...rows].sort((a, b) =>
      (sort.asc ? 1 : -1) * String(get(a)).localeCompare(String(get(b))));
  }, [rows, sort]);

  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, asc: !p.asc } : { key, asc: true }));

  const allChecked  = filteredSorted.length > 0 && filteredSorted.every((d) => selected.has(d.dockId));
  const someChecked = !allChecked && filteredSorted.some((d) => selected.has(d.dockId));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allChecked) filteredSorted.forEach((d) => next.delete(d.dockId));
    else            filteredSorted.forEach((d) => next.add(d.dockId));
    return next;
  });
  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleEdit   = (d) => setEditingDock(d);
  const handleAdd    = ()  => setAdding(true);
  const handleDelete = async (d) => {
    if (!d?.dockId) return;
    if (!window.confirm(`Delete dock "${d.dockName || d.dockId}"?`)) return;
    try {
      await dockService.deleteDock(orgId, d.dockId);
      refreshList();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Failed to delete dock');
    }
  };

  const ThSort = ({ id, label }) => (
    <th>
      <button className="data-table-sort" onClick={() => toggleSort(id)}>
        {label}
        <i className={`fas fa-sort${sort.key === id ? (sort.asc ? '-up' : '-down') : ''}`}></i>
      </button>
    </th>
  );

  return (
    <>
      <div className="sp-toolbar">
        <div className="sp-toolbar-left">
          <button
            type="button"
            className="cm-icon-btn"
            title="Search"
            aria-label="Search docks"
            onClick={() => setShowSearch((v) => !v)}
          >
            <i className="fas fa-search"></i>
          </button>
          <div className="sp-heading">
            <h1>Docks</h1>
            <div className="up-summary">
              <span className="up-summary-item"><strong>{summary.total}</strong> TOTAL</span>
              <span className="up-summary-item">
                <span className="up-dot up-dot-active" /> {summary.active} ACTIVE
              </span>
              <span className="up-summary-item">
                <span className="up-dot up-dot-inactive" /> {summary.offline} OFFLINE
              </span>
            </div>
            {showSearch && (
              <div className="cm-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search docks..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="sp-toolbar-right">
          <button type="button" className="cm-icon-btn cm-icon-btn-circle" title="More" aria-label="More">
            <i className="fas fa-ellipsis-h"></i>
          </button>
          <button type="button" className="btn-primary-pill" onClick={handleAdd}>
            <i className="fas fa-plus"></i> Add dock
          </button>
          <button type="button" className="cm-icon-btn sp-view-toggle" title="Toggle view" aria-label="Toggle view">
            <i className="fas fa-table-list"></i>
          </button>
        </div>
      </div>

      {error && <div className="users-error">{error}</div>}

      <table className="data-table cm-table sp-table">
        <thead>
          <tr>
            <th className="cm-col-check">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => { if (el) el.indeterminate = someChecked; }}
                onChange={toggleAll}
                aria-label="Select all docks"
              />
            </th>
            <ThSort id="dockName"     label="DOCK NAME" />
            <ThSort id="ipAddress"    label="DEVICE IP" />
            <ThSort id="serialNumber" label="SERIAL NUMBER" />
            <ThSort id="siteName"     label="SITE" />
            <ThSort id="firmware"     label="FIRMWARE" />
            <ThSort id="status"       label="STATUS" />
            <th className="cm-col-actions" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30 }}>
              <i className="fas fa-spinner fa-spin" /> Loading…
            </td></tr>
          )}
          {!loading && filteredSorted.length === 0 && (
            <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
              No docks found.
            </td></tr>
          )}
          {!loading && filteredSorted.map((d) => {
            const st = deriveStatus(d);
            return (
              <tr key={d.dockId}>
                <td className="cm-col-check">
                  <input
                    type="checkbox"
                    checked={selected.has(d.dockId)}
                    onChange={() => toggleRow(d.dockId)}
                    aria-label={`Select ${d.dockName || d.dockId}`}
                  />
                </td>
                <td className="up-cell-name">{d.dockName || '—'}</td>
                <td>{d.ipAddress      || <span className="up-muted">-</span>}</td>
                <td>{d.serialNumber   || <span className="up-muted">-</span>}</td>
                <td>{d.siteName       || <span className="up-muted">-</span>}</td>
                <td>{d.firmwareVersion|| <span className="up-muted">-</span>}</td>
                <td>
                  <span className={st.cls}>
                    <i className={`fas ${st.icon}`}></i> {st.label}
                  </span>
                </td>
                <td className="cm-col-actions">
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Edit dock"
                    aria-label="Edit dock"
                    onClick={() => handleEdit(d)}
                  >
                    <i className="fas fa-pen"></i>
                  </button>
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Delete dock"
                    aria-label="Delete dock"
                    onClick={() => handleDelete(d)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination controls */}
      {totalCount > PAGE_SIZE && (
        <div className="sp-pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, padding: '12px 16px' }}>
          <span style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
            {pageOffset + 1}–{Math.min(pageOffset + PAGE_SIZE, totalCount)} of {totalCount}
          </span>
          <button
            type="button"
            className="cm-icon-btn"
            disabled={pageOffset === 0}
            onClick={() => setPageOffset((p) => Math.max(0, p - PAGE_SIZE))}
            aria-label="Previous page"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <button
            type="button"
            className="cm-icon-btn"
            disabled={pageOffset + PAGE_SIZE >= totalCount}
            onClick={() => setPageOffset((p) => p + PAGE_SIZE)}
            aria-label="Next page"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      <DockDrawer
        open={!!editingDock || adding}
        dock={editingDock}
        onClose={() => { setEditingDock(null); setAdding(false); }}
        onSaved={refreshList}
      />
    </>
  );
}

